/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { Utils } from './../utils/Globals';
import Request, { HttpResponse } from '../utils/Request';
import getConfig from '../config';
import WebStorage, { WEB_STORAGE_KEYS } from '../storage/WebStorage';
import { getLogger } from '../utils/SearchLogger';
import PerformanceUtils from '../utils/PerformanceUtils';
import AnalyticsUtil from '../analytics/AnalyticsUtil';
import SearchUtils from './../utils/SearchUtils';

class FloodgateService {
	static instance: FloodgateService;
	logger: Logger;
	searchHostContext: HostContext;
	private isAlreadyFetching: boolean;
	private ffPromise: Promise<any>;
	private campaigns: Array<{
		campaign_id: string;
		variation_id: string;
		metadata: {
			[key: string]: boolean;
		};
	}>; // store all the releases
	private features: { [key: string]: boolean }; // store all the feature flag even the ones inside releases
	private floodgateStorageKeys: string;
	private hasLoggedCampaignsAndFeatures: boolean; // flag to ensure one-time logging
	private hasLoggedFloodgateFeaturesForAnalytics: boolean;

	constructor(searchHostContext: HostContext) {
		if (FloodgateService.instance) {
			return FloodgateService.instance;
		}
		this.searchHostContext = searchHostContext;
		this.logger = getLogger();
		this.isAlreadyFetching = false;
		this.campaigns = [];
		this.features = {};
		this.hasLoggedCampaignsAndFeatures = false;
		this.hasLoggedFloodgateFeaturesForAnalytics = false;
		this.floodgateStorageKeys = WEB_STORAGE_KEYS.FLOODGATE_FEATURES;
		FloodgateService.instance = this;
	}

	/* ************************** P U B L I C   M E T H O D S ***************************/

	/**
	 * Returns the list of campaigns and features from floodgate
	 * @returns {{campaigns: Array<{campaignName: testBucket}>, features: {featureName: testBucket}}}
	 */
	getFloodgateCampaignsAndFeatures(): {
		campaigns: Array<{
			campaign_id: string;
			variation_id: string;
			metadata: {
				[key: string]: boolean;
			};
		}>;
		features: { [key: string]: boolean };
	} {
		// One-time logging of campaigns and features
		if (!this.hasLoggedCampaignsAndFeatures) {
			const isAEPEnabled = SearchUtils.isFloodgateMigrated();
			const userId = SearchUtils.getUserIDForStorage();
			this.logger.info('Floodgate Service - Campaigns and Features', 'Floodgate data loaded', {
				campaignsCount: this.campaigns.length,
				featuresCount: Object.keys(this.features).length,
				campaigns: this.campaigns,
				features: this.features,
				server: true,
				userId,
				isAEPEnabled,
			});
			this.hasLoggedCampaignsAndFeatures = true;
		}

		return {
			campaigns: this.campaigns,
			features: this.features,
		};
	}

	/**
	 * fetches the Floodgate features from server.
	 * @returns {Promise<FloodgateFeatureSets>} feature experiments.
	 */
	async loadFloodgateFeatures(): Promise<FloodgateFeatureSets> {
		// this will only refetch if the timestamp is stale
		const features = await this.storeFeaturesFromReleases();
		return features;
	}

	/**
	 * Re fetches the Floodgate features if
	 * local storage's searchFloodgateFeatures timestamp is older than twenty four hours.
	 */
	async refetchFloodgateFeatures(): Promise<void> {
		const localStorageFeatures = WebStorage.localStorage.getItem(this.floodgateStorageKeys) as Record<string, any>;
		if (!localStorageFeatures) {
			this.logger.log('Floodgate Service', 'No searchFloodgateFeatures in local storage on refetch.');
			this.isAlreadyFetching = false;
			await this.storeFeaturesFromReleases();
		} else {
			this.getFeaturesFromLocalStorage();
			const currentTime = new Date();
			const timestampValidity = 4 * 60 * 60 * 1000; // 4 hours in ms: 4 hours * 60 minutes * 60 seconds * milliseconds

			const refetchTime = new Date(currentTime.getTime() - timestampValidity);
			const searchExperimentTimestamp = new Date(localStorageFeatures.timestamp);
			this.logger.log(
				'Floodgate Service',
				'Timestamp of Local Storage searchFloodgateFeatures:',
				searchExperimentTimestamp,
			);
			this.logger.log('Floodgate Service', 'Timestamp of Refetch Time:', refetchTime);
			const featureRefetchNeeded = searchExperimentTimestamp.getTime() < refetchTime.getTime();

			if (featureRefetchNeeded) {
				this.isAlreadyFetching = false;
				await this.storeFeaturesFromReleases();
			}
		}
	}

	/**
	 * Deletes the Floodgate features from local storage.
	 */
	deleteFloodgateFeaturesLocalStorage(): void {
		WebStorage.localStorage.removeItem(this.floodgateStorageKeys);

		const features = this.getFeaturesFromLocalStorage();

		// Confirm deletion
		if (features && Object.keys(features).length > 0) {
			this.logger.error('Floodgate Service', 'Failed to delete searchFloodgateFeaturess local storage.');
		}
	}

	/* ************************** P R I V A T E   M E T H O D S ***************************/

	/**
	 * Stores the features from the Floodgate Service to local storage and analytics utils.
	 */
	private async storeFeaturesFromReleases(): Promise<any> {
		try {
			if (!this.isAlreadyFetching) {
				const clientLanguage = new Utils(this.searchHostContext).getLocaleWithoutCountryCode();
				this.ffPromise = this.performFloodgateRequest(clientLanguage);
			}
			const featuresResponse = (await this.ffPromise) as Record<string, any>;
			const featureSets =
				featuresResponse instanceof Error
					? this.getFeaturesFromLocalStorage()
					: this.parseReleases(featuresResponse);
			this.setFeaturesToLocalStorage(featureSets);
			this.storeCampaignsAndFeatures(featureSets);
			this.setFeaturesToAnalytics(featureSets);
			return featureSets;
		} catch (e) {
			return {};
		}
	}

	/**
	 * Performs the request to the floodgate FeatureAPI.
	 * @param {string} clientCountry Client user country
	 * @returns {Promise<Object>} FeatureAPI request response object
	 */
	private async performFloodgateRequest(clientLanguage: string) {
		try {
			this.isAlreadyFetching = true;
			const startTime = Date.now();
			const clientId = this.searchHostContext.hostInfo.apiKey;
			const env = this.searchHostContext.environment;
			const visitorId = await SearchUtils.getVisitorGuid();

			const requestHeaders: any = {
				'x-api-key': clientId,
				'Content-Type': 'application/json',
				'x-adobe-uuid': visitorId,
				Accept: 'application/json',
			};

			if (this.searchHostContext.imsInfo.accessToken) {
				requestHeaders.Authorization = `Bearer ${this.searchHostContext.imsInfo.accessToken}`;
			}

			const hostname: string = (getConfig(env, 'SERVICES') as any).floodgate;

			const isAepEnabled = SearchUtils.isFloodgateMigrated();

			this.logger.log('Floodgate Service', `Migration decision: ${isAepEnabled ? 'AEP' : 'Sophia'}`);

			if (isAepEnabled) {
				this.logger.log('Floodgate Service', 'Using AEP endpoint from SearchHostContext:', hostname);

				const imsHeaderName = getConfig(env, 'IMS_HEADER_NAME') as string;
				const imsOrgId = getConfig(env, 'IMS_ORG_ID') as string;
				const sandboxHeaderName = getConfig(env, 'SANDBOX_HEADER_NAME') as string;
				const sandboxName = getConfig(env, 'SANDBOX_NAME') as string;

				if (imsHeaderName && imsOrgId) {
					requestHeaders[imsHeaderName] = imsOrgId;
				}
				if (sandboxHeaderName && sandboxName) {
					requestHeaders[sandboxHeaderName] = sandboxName;
				}
			} else {
				this.logger.log('Floodgate Service', 'Using Sophia endpoint from SearchHostContext:', hostname);
			}

			// eslint-disable-next-line max-len
			let path = `/api/v3/feature/?clientId=${clientId}&meta=false&userLanguage=${clientLanguage}&anonymousid=x-adobe-uuid`;

			// Adding version to that we have option to limit features for specific version
			const hostCCD =
				this.searchHostContext.hostInfo.surface === 'CCDesktop' &&
				this.searchHostContext.hostInfo.client === 'CCDesktop';

			if (hostCCD) {
				const { major = '0', minor = '0', revision = '0' } = this.searchHostContext?.hostInfo?.version || {};
				const clientVersion = `${major}.${minor}.${revision}`;
				path = `${path}&appVersion=${clientVersion}`;
			}

			const floodgateResponse = <HttpResponse<Record<string, any>>>(
				await Request.withTimeout(5000).withHeaders(requestHeaders).send('GET', `https://${hostname}${path}`)
			);

			if (floodgateResponse.isError()) {
				this.logger.error('Floodgate Service', 'Floodgate Service response error: ', {
					responseUrl: floodgateResponse.requestUrl(),
					responseStatusCode: floodgateResponse.status(),
					responseStatusMessage: floodgateResponse.statusText(),
					responseTime: Date.now() - startTime,
					performanceTiming: PerformanceUtils.getPerformanceTimingByName(floodgateResponse.requestUrl()),
				});
				const floodgateError = new Error(
					`Floodgate Service response error code: ${floodgateResponse.status()}`,
				);
				return floodgateError;
			}

			return floodgateResponse.data();
		} catch (e) {
			this.isAlreadyFetching = false;
			this.logger.error('Floodgate Service', 'Floodgate Service connect error', e.toString());
			const floodgateServiceError = new Error('Floodgate Service connect error');
			return floodgateServiceError;
		}
	}

	/**
	 * Returns the features sets for a given user from the Floodgate Service.
	 * Variant_ID = 0 means the user is in the control group for a given feature (test).
	 * Variant_ID = -1 means floodgate has no information on that user for a given feature.
	 * Variant_ID > 1 is the ID of the "bucket" for the given feature the user is in.
	 * @param {Object} featuresResponse Floodgate feature response
	 * @returns {FloodgateFeatureSets} feature sets.
	 */
	private parseReleases(featuresResponse: Record<string, any>): FloodgateFeatureSets {
		const featureSets = {};
		const negVariants: any[] = [];
		const releases = featuresResponse.releases;

		const hasReleases = releases && releases.length > 0;
		if (!hasReleases) {
			return featureSets;
		}

		for (const thisRelease of releases) {
			const {
				release_name: releaseName,
				release_analytics_params: releaseAnalyticsParams,
				features,
			} = thisRelease;

			featureSets[releaseName] = {
				features: [],
				releaseId: 0,
			};
			const negVariantFeatures: any = [];

			/**
			 * Every release has features and release_analytics_params.
			 * features is an array of feature names applicable for user.
			 * release_analytics_params is an array of objects with feature name, feature id, release id and variant id.
			 * !release_analytics_params has information on subset of features.
			 * features which are not present in release_analytics_params are considered as to be in test.
			 */

			const featureMap = new Map();

			for (const feature of features) {
				featureMap.set(feature, {
					featureName: feature || 'control',
					featureId: 0,
				});
			}

			for (const releaseAnalyticsParam of releaseAnalyticsParams) {
				const {
					release_id: releaseId,
					variant_id: variantId,
					f_key: feature,
					feature_id: featureId,
				} = releaseAnalyticsParam;

				featureSets[releaseName].releaseId = releaseId;

				if (variantId >= 0) {
					featureMap.set(feature, {
						featureName: feature || 'control',
						featureId: featureId || 0,
						variantId,
					});
				} else {
					// Add this feature to the log for variant IDs < 0
					negVariantFeatures.push(feature || 'none');
				}
			}

			featureSets[releaseName].features = Array.from(featureMap.values());
			// If we had some features with variant IDs of -1, store them here for logging later
			if (negVariantFeatures.length > 0) {
				negVariants.push({
					[releaseName]: negVariantFeatures,
				});
			}
		}

		// If we had some features with variant IDs of -1, log them for reference
		if (negVariants.length > 0) {
			this.logger.log(
				'Floodgate Service',
				'Negative Variants received from Floodgate (ExperimentName: Feature): ',
				negVariants,
			);
		}

		return featureSets;
	}

	/**
	 * Stores the features sets for a given user to AnalyticsUtils
	 * @param {Object} featureSets user feature sets
	 */
	private setFeaturesToAnalytics(featureSets: Record<string, any> = {}): void {
		if (featureSets && Object.keys(featureSets).length) {
			if (AnalyticsUtil.experiment) {
				const analyticsObject = {};

				for (const thisExperiment in featureSets) {
					const featuresArray = featureSets[thisExperiment].features;

					for (const feature of featuresArray) {
						const validVariantId = feature.variantId > 0;
						const hasNoVariantId = !('variantId' in feature);
						const splitFeatureName = feature.featureName.includes('-')
							? feature.featureName.split('-')[0]
							: feature.featureName;

						let key = thisExperiment;
						const value = validVariantId || hasNoVariantId ? 'test' : 'control';
						thisExperiment === '||features||' && (key = splitFeatureName);
						analyticsObject[key] = value;
					}
				}

				if (!this.hasLoggedFloodgateFeaturesForAnalytics) {
					const isAEPEnabled = SearchUtils.isFloodgateMigrated();
					this.logger.info('Floodgate Service - Analytics Grouping', 'Floodgate data for Analytics loaded', {
						analyticsObject,
						server: true,
						isAEPEnabled,
					});
					this.hasLoggedFloodgateFeaturesForAnalytics = true;
				}
				AnalyticsUtil.experiment = Object.assign(AnalyticsUtil.experiment, analyticsObject);
			}
		}
	}

	/**
	 * Stores the features sets for a given user to local storage
	 * @param {Object} experiments user feature sets
	 */
	private setFeaturesToLocalStorage(experiments: Record<string, any>): void {
		const featureExperiments = {
			experiments,
			timestamp: new Date().toISOString(),
		};
		experiments && WebStorage.localStorage.setItem(this.floodgateStorageKeys, featureExperiments);
	}

	/**
	 * Retrieves the features sets for a given user from local storage
	 * @returns {FloodgateFeatureSets} user feature sets.
	 */
	private getFeaturesFromLocalStorage(): FloodgateFeatureSets {
		let retVal = {};
		const fgExperiments = WebStorage.localStorage.getItem(this.floodgateStorageKeys) as FloodgateFeatureSets;

		if (fgExperiments) {
			retVal = fgExperiments.experiments;
		}

		return retVal;
	}

	private storeCampaignsAndFeatures(floodgateExperiments: FloodgateFeatureSets = {}): void {
		const floodgateCampaigns: Array<{
			campaign_id: string;
			variation_id: string;
			metadata: {
				[key: string]: boolean;
			};
		}> = [];

		const floodgateFeatures: { [key: string]: boolean } = {};

		const floodgateExperimentsList = Object.keys(floodgateExperiments);

		for (const experiment of floodgateExperimentsList) {
			let experimentVariantId = 'test';

			const experimentHasFeatures =
				floodgateExperiments[experiment] &&
				Array.isArray(floodgateExperiments[experiment].features) &&
				floodgateExperiments[experiment].features.length > 0;

			if (!experimentHasFeatures) {
				floodgateCampaigns.push({
					campaign_id: experiment,
					variation_id: 'control',
					metadata: {},
				});
				continue;
			}

			const experimentMetaData: { [key: string]: boolean } = {};
			for (const feature of floodgateExperiments[experiment].features) {
				const validVariantId = feature?.variantId > 0 ?? false;
				const hasNoVariantId = !('variantId' in feature);
				const featureName = feature.featureName.includes('-')
					? feature.featureName.split('-')[0]
					: feature.featureName;

				floodgateFeatures[featureName] = validVariantId || hasNoVariantId;
				experimentMetaData[featureName] = validVariantId || hasNoVariantId;
				!validVariantId && (experimentVariantId = 'control');
			}

			floodgateCampaigns.push({
				campaign_id: experiment,
				variation_id: experiment === '||features||' ? 'test' : experimentVariantId,
				metadata: experimentMetaData,
			});
		}

		this.campaigns = floodgateCampaigns;
		this.features = floodgateFeatures;
	}
}

export default FloodgateService;
